import { Component } from "@theme/component";
import { fetchConfig } from "@theme/utilities";
import { ThemeEvents, CartAddEvent, CartUpdateEvent, CartErrorEvent, VariantUpdateEvent } from "@theme/events";

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /**
   * 리스너 해제용 컨트롤러.
   *
   * 종전에는 add 와 remove 가 각각 `.bind(this)` 를 호출했다. bind 는 매번 새 함수
   * 객체를 만들므로 remove 가 아무것도 지우지 못하고 document 리스너가 영구 누적됐다.
   * 이 파일 109행대가 이미 `{ signal }` 패턴을 쓰고 있어 같은 방식으로 맞춘다.
   */
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;

    // Listen for cart add events to remove loading state
    document.addEventListener(CartAddEvent.eventName, this.#onCartAddDone.bind(this), { signal });

    // Listen for cart error events to remove loading state
    document.addEventListener(CartErrorEvent.eventName, this.#onCartAddDone.bind(this), { signal });

    super.connectedCallback();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
    super.disconnectedCallback();
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   *
   * PFAS: styles the materials team judged unsellable in the US stay disabled no
   * matter what the variant state says. Without this guard a variant change
   * re-enables the button and the Liquid-side block is lost.
   */
  enable() {
    if (this.refs.addToCartButton.dataset.pfasUsBlocked === "true") return;
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Adds loading class to the add to cart button.
   */
  addLoading() {
    this.refs.addToCartButton.classList.add("btn--loading");
    this.refs.addToCartSpinner.classList.remove("hidden");
  }

  /**
   * Removes loading class from the add to cart button.
   */
  removeLoading() {
    this.refs.addToCartButton.classList.remove("btn--loading");
    this.refs.addToCartSpinner.classList.add("hidden");
  }

  /**
   * Handles cart add completion (success or error).
   * Removes loading state from the button.
   */
  #onCartAddDone() {
    this.removeLoading();
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /**
   * 진행 중인 카트 추가 submit이 있는지 추적.
   * handleSubmit의 fetch 시작 시 true, .finally에서 false.
   * variant-update 기인 removeLoading이 in-flight submit의 스피너를 가로채
   * 끄지 않도록 가드한다(F-PF2).
   * @type {boolean}
   */
  #submitInFlight = false;

  /** @type {number | undefined} 자기치유 로딩 해제 타이머(F-PF1: 이벤트 미도착 stuck 방지) */
  #selectedLoadingTimeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    // abort 한 컨트롤러를 다시 만든다. 필드 초기화로 만든 컨트롤러는 생성 때 한 번만
    // 실행되므로, 재생성하지 않으면 요소가 다시 붙을 때 이미 중단된 signal 로
    // 등록하게 되고 리스너가 **조용히 안 붙는다**(사양상 no-op).
    // 상품 카드는 카트·검색 드로어로 옮겨지므로 재연결이 실제로 일어난다.
    this.#abortController = new AbortController();
    // 분리 시 보류 타이머 정리, detached 요소에 대한 콜백 발화/누수 방지.
    if (this.#selectedLoadingTimeout) clearTimeout(this.#selectedLoadingTimeout);
    if (this.#timeout) clearTimeout(this.#timeout);
    if (this.#optimisticRestoreTimeout) clearTimeout(this.#optimisticRestoreTimeout);
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // 같은 스코프의 variant-picker, 선택 상태/정확매칭 id의 단일 진실(아래 가드 2곳에서 공유).
    const picker = this.closest(".shopify-section, dialog, product-card")?.querySelector("variant-picker");

    // 선택 미완결 가드(KS: 사이즈 미선택), 단일 진실은 variant-picker의 선택 상태.
    // disabled 체크보다 먼저 둔다(F-PF6): 전 사이즈 품절 컬러처럼 버튼이 disabled여도
    // 사용자에게 '사이즈를 선택하세요' 피드백을 줘야 하기 때문. 미완결이면 차단+피드백.
    if (this.#blockIfSelectionIncomplete(picker)) return;

    // Check if the add to cart button is disabled and do an early return if it is
    // (boolean 속성은 getAttribute가 ""를 반환하므로 property로 검사
    //  버튼 클릭이 아닌 Enter 키 submit 경로에서도 가드가 동작해야 한다)
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // (F-PF7) stale variantId 방어: 완결 선택의 정확매칭 id와 form 값이 어긋나면
    // (background 갱신 유실·경쟁으로 form 미동기) submit 직전에 라이브 선택 기준으로
    // 교정한다. 오래된/잘못된 variant 커밋 방지. picker/맵 없으면(degrade) 그대로 둔다.
    const resolvedId = /** @type {any} */ (picker)?.resolvedVariantId ?? null;
    if (resolvedId != null && String(this.refs.variantId.value) !== String(resolvedId)) {
      this.refs.variantId.value = String(resolvedId);
    }

    // 진행 중 submit 이 있으면 무시한다(Optimistic 상태에서는 스피너가 없어 연타가
    // 그대로 새 요청이 되므로 여기서 막는다).
    if (this.#submitInFlight) return;

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    const formData = new FormData(form);

    // KS v3.9.156 Optimistic UI: 서버 응답을 기다리지 않고 헤더 뱃지와 버튼 문구를
    // 먼저 바꾼다. 요청 자체는 그대로 보내고, 실패(422·네트워크)면 되돌린다.
    // 드로어 열기·드로어 본문은 서버가 그려준 sections 가 정본이라 그대로 기다린다.
    const optimisticQty = Number(formData.get("quantity")) || Number(this.dataset.quantityDefault) || 1;
    this.#applyOptimistic(optimisticQty);

    // Request sections for cart-items components
    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    if (cartItemComponentsSectionIds.length > 0) {
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    }

    const fetchCfg = fetchConfig("javascript", { body: formData });

    // 이 시점부터 스피너 소유권은 카트 submit에 있다. 지연된 variant-update의
    // removeLoading이 POST 진행 중에 스피너를 끄지 못하도록 표시(F-PF2).
    // 동기 throw 가능 지점(form 체크)을 모두 지난 fetch 직전에 둬, 예외 시
    // .finally 미도달로 플래그가 영구 latch되는 것을 막는다.
    this.#submitInFlight = true;

    fetch(FoxTheme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "text/html",
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.#revertOptimistic();
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute("id") || "", response.message, response.description, response.errors)
          );

          this.refs.addToCartButtonContainer?.removeLoading();

          if (!addToCartTextError) return;

          // Clear existing timeout to reset the 10s timer
          if (this.#timeout) {
            clearTimeout(this.#timeout);
          }

          // Set error message (replaces any existing message)
          addToCartTextError.textContent = response.description || response.message;
          addToCartTextError.classList.remove("hidden");

          // Announce error for screen readers
          this.#setLiveRegionText(response.message);

          // Hide error message after 10 seconds
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");
            this.#clearLiveRegionText();
          }, 10000);

          // When error occurs (e.g. adding more items than available),
          // backend still adds the max allowed amount to cart.
          // Dispatch CartUpdateEvent with actual count for cart count sync.
          if (response.sections) {
            let actualItemCount = 0;
            let foundCount = false;

            for (const section of Object.values(response.sections)) {
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = section;

              // Try cart-items ref first, fallback to cart-count bubble
              let itemCountElement = tempDiv.querySelector('[ref="cartItemCount"]');
              if (!itemCountElement) {
                itemCountElement = tempDiv.querySelector(".cart-bubble__text-count");
              }

              if (itemCountElement) {
                actualItemCount = parseInt(itemCountElement.textContent || "0", 10);
                foundCount = true;
                break;
              }
            }
            if (foundCount) {
              this.dispatchEvent(
                new CartUpdateEvent({}, this.id, {
                  itemCount: actualItemCount,
                  source: "product-form-component",
                  productId: this.dataset.productId,
                  sections: response.sections,
                })
              );
            }
          } else {
            // Shopify doesn't return sections on 422 errors
            // Fetch cart.js to get actual count (edge case: add more than available)
            fetch(FoxTheme.routes.cart)
              .then((res) => res.json())
              .then((cart) => {
                this.dispatchEvent(
                  new CartUpdateEvent({}, this.id, {
                    itemCount: cart.item_count || 0,
                    source: "product-form-component",
                    productId: this.dataset.productId,
                  })
                );
              })
              .catch((error) => {
                console.error("Failed to fetch cart count:", error);
              });
          }

          return;
        } else {
          const id = formData.get("id");

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          if (!id) throw new Error("Form ID is required");

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addedText = FoxTheme.translations.added;
            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: "product-form-component",
              itemCount: Number(formData.get("quantity")) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          // 성공: "Added" 문구를 잠깐 두었다가 원래 문구로 돌린다.
          this.#scheduleOptimisticLabelRestore();
        }
      })
      .catch((error) => {
        console.error(error);
        this.#revertOptimistic();
        // KS v3.9.53(SP "카트 버튼 무한 로딩"): 네트워크 단 실패(fetch reject·비JSON
        // 응답)는 CartAdd/CartError 어느 이벤트도 안 떠서 스피너가 영구 잔류하던
        // 견고성 구멍, 직접 해제하고 에러를 표기한다(성공/API 에러 경로와 대칭).
        this.refs.addToCartButtonContainer?.removeLoading();
        if (addToCartTextError) {
          const msg = 'Connection error. Please try again.';
          addToCartTextError.textContent = msg;
          addToCartTextError.classList.remove('hidden');
          this.#setLiveRegionText(msg);
          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, 10000);
        }
      })
      .finally(() => {
        // submit 종료, 스피너 소유권 해제(이후 variant-update가 정상 해제 가능).
        // 로딩 자체는 #onCartAddDone(CartAdd/CartError)이 해제한다(네트워크 실패는
        // 위 catch가 직접 해제).
        this.#submitInFlight = false;
        // add more thing to do in here if needed.
        // cartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  /** Optimistic 적용 전 버튼 원문. null 이면 적용 중이 아니다. @type {string|null} */
  #optimisticPrevLabel = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #optimisticRestoreTimeout;
  /** Optimistic 으로 우리가 연 드로어(실패 시 닫기 위해). @type {any} */
  #optimisticOpenedDrawer = null;

  /** 버튼 문구 span. 마크업은 snippets/add-to-cart-button.liquid */
  #labelEl() {
    return this.refs.addToCartButtonContainer?.refs.addToCartButton?.querySelector(".add-to-cart-text__content") ?? null;
  }

  /**
   * 헤더 뱃지 +qty, 버튼 문구 "Added". 스피너는 켜지 않는다(문구가 곧 피드백).
   * @param {number} qty
   */
  #applyOptimistic(qty) {
    document.querySelectorAll('cart-count:not([data-context="drawer"])').forEach((el) => {
      /** @type {any} */ (el).applyOptimisticDelta?.(qty);
    });
    // 드로어도 먼저 연다(SP 09-21 "드로어가 엄청 느리게 뜬다"). 본문은 서버 sections 가
    // 오면 morph 로 갈아끼워지므로, 그때까지 기존 항목을 비활성 톤으로 보여준다.
    // 우리가 연 경우만 기억해 실패 시 다시 닫는다.
    const drawer = /** @type {any} */ (document.querySelector("cart-drawer-component[auto-open]"));
    if (drawer && drawer.refs?.dialog && !drawer.refs.dialog.open && typeof drawer.showDialog === "function") {
      drawer.showDialog();
      this.#optimisticOpenedDrawer = drawer;
    }
    document.querySelectorAll("cart-items-component").forEach((el) => el.classList.add("cart-items-disabled"));
    const label = this.#labelEl();
    const addedText = FoxTheme.translations?.added;
    if (label && addedText && this.#optimisticPrevLabel === null) {
      this.#optimisticPrevLabel = label.textContent;
      label.textContent = addedText;
      this.refs.addToCartButtonContainer?.classList.add("is-added");
    }
  }

  /** 실패: 뱃지 되돌리고 버튼 문구 즉시 복원 */
  #revertOptimistic() {
    document.querySelectorAll('cart-count:not([data-context="drawer"])').forEach((el) => {
      /** @type {any} */ (el).revertOptimistic?.();
    });
    this.#clearCartItemsPending();
    const drawer = this.#optimisticOpenedDrawer;
    this.#optimisticOpenedDrawer = null;
    if (drawer && drawer.refs?.dialog?.open && typeof drawer.closeDialog === "function") drawer.closeDialog();
    this.#restoreOptimisticLabel();
  }

  #clearCartItemsPending() {
    document.querySelectorAll("cart-items-component").forEach((el) => el.classList.remove("cart-items-disabled"));
  }

  /** 성공: 1.2초 뒤 문구 복원(그 사이 variant-update 가 문구를 바꿨으면 건드리지 않는다) */
  #scheduleOptimisticLabelRestore() {
    this.#optimisticOpenedDrawer = null;
    this.#clearCartItemsPending();
    if (this.#optimisticRestoreTimeout) clearTimeout(this.#optimisticRestoreTimeout);
    this.#optimisticRestoreTimeout = setTimeout(() => this.#restoreOptimisticLabel(), 1200);
  }

  #restoreOptimisticLabel() {
    if (this.#optimisticRestoreTimeout) {
      clearTimeout(this.#optimisticRestoreTimeout);
      this.#optimisticRestoreTimeout = undefined;
    }
    const prev = this.#optimisticPrevLabel;
    this.#optimisticPrevLabel = null;
    this.refs.addToCartButtonContainer?.classList.remove("is-added");
    const label = this.#labelEl();
    if (!label || prev === null) return;
    // 우리가 넣은 "Added" 가 그대로 있을 때만 되돌린다(다른 갱신이 덮었으면 그쪽이 최신).
    if (label.textContent === FoxTheme.translations?.added) label.textContent = prev;
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      // 다른 상품의 갱신이지만, 같은 스코프에서 variantSelected가 켠 로딩이
      // 남아 있을 수 있다(상품 전환 stale 이벤트). submit 진행 중이 아니면
      // 스피너를 해제해 영구 스피너를 방지한다(F-PF1).
      if (!this.#submitInFlight) this.refs.addToCartButtonContainer?.removeLoading();
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;

    if (!currentAddToCartButton) return;

    // 실제 갱신 도착, variantSelected가 건 자기치유 타이머는 더 이상 불필요.
    if (this.#selectedLoadingTimeout) clearTimeout(this.#selectedLoadingTimeout);

    // 갱신 도착, variantSelected에서 켠 로딩 상태 해제.
    // 단, 카트 submit이 진행 중이면(지연된 background variantUpdate가 POST
    // 중에 도착) 스피너 소유권은 submit에 있으므로 끄지 않는다(F-PF2).
    if (!this.#submitInFlight) addToCartButtonContainer.removeLoading();

    // 선택 미완결(KS: 사이즈 미선택): 어떤 variant도 form에 커밋하지 않는다.
    // 대표 variant의 가용성은 무시하고, 현재 부분 선택 범위 내 구매 가능 조합
    // 유무(anyAvailable)로만 버튼을 칠한다. 미선택 상태의 오인 sold out 방지.
    // undefined(legacy 생산자)는 기존 동작 유지.
    if (event.detail.data.selectionComplete === false) {
      this.dataset.selectionComplete = "false";
      variantId.value = ""; // fail-safe: submit이 새어도 잘못된 variant가 담기지 않는다
      const noneAvailable = event.detail.data.anyAvailable === false;

      if (noneAvailable) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }
      // 가속 결제는 구체 variant를 요구하므로 미완결 동안 숨김 유지
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute("hidden", "true");
      // sold out vs unavailable 구분(F-PF3): 부분 선택엔 단일 해석 variant가 없으므로
      // 생산자가 cache.map(row.im)에서 집계한 anySoldOut으로 판정. undefined(legacy/
      // im 없는 캐시)면 기존 동작(sold out) 유지. 가용 조합 있으면(!noneAvailable) 무시.
      const isSoldOut = noneAvailable ? event.detail.data.anySoldOut !== false : false;
      this.#updateButtonTextOptimistic(currentAddToCartButton, !noneAvailable, isSoldOut);
      return;
    }
    // 선택이 완결되면 이전에 #blockIfSelectionIncomplete가 띄운 'Please select a size'
    // 안내가 남아 있을 수 있다. 유효한 사이즈를 고른 순간 즉시 정리한다(F-PF4).
    // 단, 현재 표시 메시지가 select-size 안내일 때만 숨겨 다른 에러(품절 등)는 보존.
    const { addToCartTextError } = this.refs;
    const selectSizeText =
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.dataset.selectSizeText || "Please select a size";
    if (addToCartTextError && addToCartTextError.textContent === selectSizeText) {
      addToCartTextError.classList.add("hidden");
      if (this.#timeout) {
        clearTimeout(this.#timeout);
        this.#timeout = undefined;
      }
      this.#clearLiveRegionText();
    }

    // selectionComplete === true(명시적 완결)일 때만 dataset을 'true'로 승격(F-PF5).
    // undefined(legacy 생산자)는 로드 시 인라인 스크립트가 심은 'false'를 보존해야
    // 하므로 건드리지 않는다. 안 그러면 미선택 상태가 조용히 'true'로 덮인다.
    if (event.detail.data.selectionComplete === true) {
      this.dataset.selectionComplete = "true";
    }

    // Update the button state and text optimistically (always do this, even from cache)
    const variant = event.detail.resource;
    const isAvailable = variant?.available ?? false;

    // Determine correct unavailable state
    // Sold out: Has inventory management (shopify) → inventory = 0
    // Unavailable: No inventory management OR variant is null/undefined → other reasons
    const isSoldOut = !isAvailable && variant && variant.inventory_management === "shopify";

    if (!isAvailable) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute("hidden", "true");
    } else {
      addToCartButtonContainer.enable();
      // PFAS: express checkout would bypass the disabled add-to-cart button.
      if (this.refs.acceleratedCheckoutButtonContainer?.dataset.pfasUsBlocked !== "true") {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
      }
    }

    // Update button text optimistically (works for both available and unavailable)
    // This handles ALL cases: cache hit, JSON fetch, and HTML fallback
    this.#updateButtonTextOptimistic(currentAddToCartButton, isAvailable, isSoldOut);

    // Update the variant ID (always do this)
    if (event.detail.resource?.id) {
      variantId.value = event.detail.resource.id ?? "";
    } else {
      variantId.value = "";
    }
  };

  /**
   * Updates button text optimistically based on variant availability
   * @param {HTMLElement} button - The add to cart button element
   * @param {boolean} isAvailable - Whether the variant is available
   * @param {boolean} isSoldOut - Whether the variant is sold out (has inventory_management = "shopify")
   */
  #updateButtonTextOptimistic(button, isAvailable, isSoldOut = false) {
    if (!button) return;

    const textElement = button.querySelector(".add-to-cart-text__content");
    const iconTextElement = button.querySelector(".btn__icon-text");

    if (!textElement) return;

    // PFAS: keep the US-blocked label through every variant change.
    if (button.dataset.pfasUsBlocked === "true") {
      const blockedText = button.dataset.pfasUnavailableText || "Not available in the US";
      textElement.textContent = blockedText;
      const blockedIconText = button.querySelector(".btn__icon-text");
      if (blockedIconText) blockedIconText.textContent = blockedText;
      return;
    }

    // Get text from data attributes (translations from Liquid)
    const availableText = button.dataset.addToCartText || "Add to cart";
    const soldOutText = button.dataset.soldOutText || "Sold out";
    const unavailableText = button.dataset.unavailableText || "Unavailable";

    // Determine correct text based on state
    // - Available: variant.available = true → "Add to cart"
    // - Sold out: variant.available = false + inventory_management = "shopify" → "Sold out"
    // - Unavailable: variant.available = false + no inventory_management → "Unavailable"
    let newText;
    if (isAvailable) {
      newText = availableText;
    } else if (isSoldOut) {
      newText = soldOutText;
    } else {
      newText = unavailableText;
    }

    // Update both text content and icon text (if present)
    textElement.textContent = newText;
    if (iconTextElement) {
      iconTextElement.textContent = newText;
    }
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   * 로딩 상태(스피너)도 함께 표시, 갱신 완료(#onVariantUpdate) 전까지 버튼이
   * 잘못된 상태(이전 variant 기준 텍스트/활성화)로 노출되지 않도록 한다.
   * 캐시 히트 시 갱신이 동기적으로 도착하므로 체감 깜빡임은 없다.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
    this.refs.addToCartButtonContainer?.addLoading();

    // 자기치유 안전장치(F-PF1): 캐시 미스 fallback(variant-picker.js의 variantId/캐시
    // 없음 else 분기)이나 background HTML fetch가 abort/reject되면 후속
    // VariantUpdateEvent가 dispatch되지 않아 removeLoading이 영영 안 불릴 수 있다
    // (rapid clicking이 in-flight 요청을 abort) → 영구 스피너. submit이 진행 중이
    // 아니면 짧은 유예 후 로딩을 강제 해제한다. 정상 경로는 갱신이 5s 안에 도착해
    // #onVariantUpdate가 이 타이머를 먼저 clear하므로 체감 영향 없음.
    if (this.#selectedLoadingTimeout) clearTimeout(this.#selectedLoadingTimeout);
    this.#selectedLoadingTimeout = setTimeout(() => {
      if (!this.#submitInFlight) this.refs.addToCartButtonContainer?.removeLoading();
    }, 5000);
  };

  /**
   * 선택 미완결(KS: 사이즈 미선택) 시 submit을 차단하고 선택 유도 피드백을 표시한다.
   * 진실 공급원: 같은 스코프의 variant-picker 선택 상태(isSelectionComplete).
   * picker가 없거나 컴포넌트 미초기화면 마지막 variantUpdate가 남긴 dataset 플래그로 폴백.
   * @param {Element|null} [picker] - 같은 스코프의 variant-picker (handleSubmit이 조회해 전달)
   * @returns {boolean} true면 차단됨
   */
  #blockIfSelectionIncomplete(picker) {
    const incomplete =
      picker && typeof (/** @type {any} */ (picker).isSelectionComplete) === "boolean"
        ? !(/** @type {any} */ (picker).isSelectionComplete)
        : this.dataset.selectionComplete === "false";

    if (!incomplete) return false;

    const { addToCartTextError } = this.refs;
    const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    const message = button?.dataset.selectSizeText || "Please select a size";

    if (addToCartTextError) {
      addToCartTextError.textContent = message;
      addToCartTextError.classList.remove("hidden");
      this.#timeout = setTimeout(() => {
        addToCartTextError.classList.add("hidden");
        this.#clearLiveRegionText();
      }, 10000);
    }
    this.#setLiveRegionText(message);

    // 미선택 옵션 그룹 하이라이트 + 스크롤
    if (picker) {
      const group = Array.from(picker.querySelectorAll(".variant-option--fieldset")).find(
        (g) => !g.querySelector("input:checked")
      );
      if (group) {
        group.classList.remove("variant-option--attention");
        // reflow로 애니메이션 재시작
        void (/** @type {HTMLElement} */ (group).offsetWidth);
        group.classList.add("variant-option--attention");
        group.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }

    return true;
  }
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}
